<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page accounts-payable-summary-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="统计期间" required>
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="供应商">
                <a-select
                  v-model="query.supplierId"
                  allow-clear
                  show-search
                  option-filter-prop="children"
                  placeholder="全部供应商">
                  <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                    {{ item.supplierCode }} · {{ item.supplierName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="关键字">
                <a-input v-model="query.keyword" allow-clear placeholder="编码或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>应付账款汇总表</h3>
            <p>按供应商汇总：应付金额取入库单头 total_amount（与付款单核销来源一致）；期初 = 开始前已过账入库 − 已核销付款；本期已付仅含核销入库的付款，不含预付款。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="accounts-payable-summary-totals">
              合计 期初 <strong class="erp-money">¥{{ formatMoney(sumOpeningBalance) }}</strong>
              应付 <strong class="erp-money">¥{{ formatMoney(sumPeriodPayable) }}</strong>
              已付 <strong class="erp-money">¥{{ formatMoney(sumPeriodPaid) }}</strong>
              期末 <strong class="erp-money">¥{{ formatMoney(sumClosingBalance) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:accounts-payable-summary:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无数据' }"
          @change="onPageChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pageAccountsPayableSummaryReport,
  listAccountsPayableSummarySupplierOptions,
  exportAccountsPayableSummaryReport
} from '@/api/erp/accountsPayableSummaryReport'

const emptyQuery = () => ({
  supplierId: undefined,
  keyword: ''
})

export default {
  name: 'ErpAccountsPayableSummaryReport',
  data () {
    return {
      dateRange: [moment().startOf('month'), moment().endOf('month')],
      query: emptyQuery(),
      supplierOptions: [],
      rows: [],
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true, showTotal: t => `共 ${t} 条` },
      sumOpeningBalance: 0,
      sumPeriodPayable: 0,
      sumPeriodPaid: 0,
      sumClosingBalance: 0,
      tableScroll: { x: 960 },
      tableColumns: [
        { title: '供应商编码', dataIndex: 'supplierCode', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '供应商名称', dataIndex: 'supplierName', width: 220, scopedSlots: { customRender: 'text' } },
        { title: '期初余额', dataIndex: 'openingBalance', width: 130, align: 'right', scopedSlots: { customRender: 'money' } },
        { title: '本期应付', dataIndex: 'periodPayable', width: 130, align: 'right', scopedSlots: { customRender: 'money' } },
        { title: '本期已付', dataIndex: 'periodPaid', width: 130, align: 'right', scopedSlots: { customRender: 'money' } },
        { title: '期末余额', dataIndex: 'closingBalance', width: 130, align: 'right', scopedSlots: { customRender: 'money' } }
      ]
    }
  },
  created () {
    this.loadSupplierOptions()
    this.loadData()
  },
  methods: {
    displayValue (v) {
      return v == null || v === '' ? '—' : v
    },
    formatMoney (v) {
      const n = Number(v)
      if (Number.isNaN(n)) return '0.00'
      return n.toFixed(2)
    },
    queryParams () {
      const range = this.dateRange || []
      return {
        ...this.query,
        dateStart: range[0] ? range[0].format('YYYY-MM-DD') : undefined,
        dateEnd: range[1] ? range[1].format('YYYY-MM-DD') : undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadSupplierOptions () {
      listAccountsPayableSummarySupplierOptions().then(res => {
        this.supplierOptions = res.data || []
      })
    },
    loadData () {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择统计期间')
        return
      }
      this.loading = true
      pageAccountsPayableSummaryReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = data.records || []
        this.pagination.total = Number(data.total || 0)
        this.pagination.current = Number(data.current || 1)
        this.pagination.pageSize = Number(data.size || 20)
        this.sumOpeningBalance = data.sumOpeningBalance
        this.sumPeriodPayable = data.sumPeriodPayable
        this.sumPeriodPaid = data.sumPeriodPaid
        this.sumClosingBalance = data.sumClosingBalance
      }).finally(() => {
        this.loading = false
      })
    },
    search () {
      this.pagination.current = 1
      this.loadData()
    },
    resetQuery () {
      this.dateRange = [moment().startOf('month'), moment().endOf('month')]
      this.query = emptyQuery()
      this.search()
    },
    onPageChange (pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.loadData()
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportAccountsPayableSummaryReport(params)
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `应付账款汇总_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.accounts-payable-summary-totals {
  margin-right: 12px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  strong {
    margin: 0 8px 0 2px;
  }
}
</style>
